import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getDirForLanguage } from "../../lib/utils";
import { X } from "lucide-react";
import {
  checkOverlap,
  computeSelectionOffsets,
  computeSelectionOffsetsFromDom,
  renderAnnotatedText,
  renderAnnotatedHtml,
  isHtmlContent,
  htmlToPlainText,
} from "./annotatableTextUtils/index";

export interface Reaction {
  key: string;
  label: string;
  color: string;
  textColor: string;
}

export interface Markup {
  id: number;
  start: number;
  end: number;
  text: string;
  reactionKey: string;
  comment?: string;
  createdAt: number;
}

export const DEFAULT_REACTIONS: Reaction[] = [
  {
    key: "agree",
    label: "Agree",
    color: "bg-green-200",
    textColor: "text-green-800",
  },
  {
    key: "disagree",
    label: "Disagree",
    color: "bg-red-200",
    textColor: "text-red-800",
  },
  {
    key: "question",
    label: "Question",
    color: "bg-yellow-200",
    textColor: "text-yellow-800",
  },
  {
    key: "unclear",
    label: "Unclear",
    color: "bg-orange-200",
    textColor: "text-orange-800",
  },
  {
    key: "suggest",
    label: "Suggest",
    color: "bg-blue-200",
    textColor: "text-blue-800",
  },
];

interface SelectedText {
  text: string;
  start: number;
  end: number;
  rect: { x: number; y: number };
}

interface TooltipMenu {
  x: number;
  y: number;
  placement: "above" | "below";
}

const POPOVER_MIN_TOP_SPACE_PX = 140;

function computePopoverAnchor(rect: DOMRect): {
  x: number;
  y: number;
  placement: "above" | "below";
} {
  const cx = rect.left + rect.width / 2;
  const x = Math.min(window.innerWidth - 120, Math.max(120, cx));
  const placeAbove = rect.top >= POPOVER_MIN_TOP_SPACE_PX;
  if (placeAbove) {
    return { x, y: rect.top - 8, placement: "above" };
  }
  return { x, y: rect.bottom + 10, placement: "below" };
}

interface TextMarkupProps {
  content: {
    key: string;
    label?: string;
    proposalText?: string;
    commentRequired?: boolean;
    reactions?: Reaction[];
  };
  userData?: {
    markups?: Markup[];
    markupCount?: number;
    totalCommentLength?: number;
  };
  initialValue?: {
    markups?: Markup[];
    markupCount?: number;
    totalCommentLength?: number;
  };
  onChange?: (key: string, value: object) => void;
  readonly?: boolean;
  /** Content direction for proposal text (e.g. from prescription language). Enables RTL for Hebrew. */
  dir?: "ltr" | "rtl";
}

const TextMarkup: React.FC<TextMarkupProps> = ({
  content,
  userData,
  initialValue,
  onChange,
  readonly = false,
  dir: contentDir,
}) => {
  const { i18n } = useTranslation();
  const effectiveDir: "ltr" | "rtl" =
    getDirForLanguage(i18n.language) === "rtl" || contentDir === "rtl"
      ? "rtl"
      : (contentDir ?? getDirForLanguage(i18n.language));
  const proposalText = content.proposalText ?? "";
  const reactions =
    content.reactions && content.reactions.length > 0
      ? content.reactions
      : DEFAULT_REACTIONS;

  const stored = userData ?? initialValue ?? {};
  const [markups, setMarkups] = useState<Markup[]>(stored.markups ?? []);

  // Sync markups when stored data changes (e.g. in review mode when switching panes or data loads)
  useEffect(() => {
    if (readonly) {
      const next = (userData ?? initialValue ?? {}).markups ?? [];
      setMarkups(next);
    }
  }, [readonly, userData, initialValue]);

  const [pendingComment, setPendingComment] = useState<string>("");
  const [pendingReactionKey, setPendingReactionKey] = useState<string | null>(null);
  const [editingMarkupId, setEditingMarkupId] = useState<number | null>(null);
  const [tooltipMenu, setTooltipMenu] = useState<TooltipMenu | null>(null);
  const [selectedText, setSelectedText] = useState<SelectedText | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  /** After touch selection, ignore synthetic mouseup for a short window (iOS Safari). */
  const blockMouseUpUntilRef = useRef(0);

  const buildValue = (nextMarkups: Markup[]) => {
    const markupCount = nextMarkups.length;
    const totalCommentLength = nextMarkups.reduce(
      (sum, m) => sum + (m.comment || "").length,
      0,
    );
    return {
      markups: nextMarkups,
      markupCount,
      totalCommentLength,
    };
  };

  useEffect(() => {
    if (!readonly && onChange && content.key) {
      onChange(content.key, buildValue(markups));
    }
  }, [markups, readonly, onChange, content.key]);

  const handleTextSelection = useCallback(() => {
    if (readonly || !proposalText) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = textRef.current;
    if (!container) return;

    if (!container.contains(range.commonAncestorContainer)) return;

    if (range.collapsed) {
      setTooltipMenu(null);
      setSelectedText(null);
      setPendingComment("");
      return;
    }

    const selectedTextContent = selection.toString().trim();

    if (selectedTextContent.length === 0) {
      setTooltipMenu(null);
      setSelectedText(null);
      setPendingComment("");
      return;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) return;

    const textContent = container.textContent ?? "";
    const offsets = isHtmlContent(proposalText)
      ? computeSelectionOffsetsFromDom(container, selection)
      : computeSelectionOffsets(
          textContent,
          selectedTextContent,
          typeof range.startOffset === "number" ? range.startOffset : 0,
        );

    if (!offsets) {
      setTooltipMenu(null);
      setSelectedText(null);
      return;
    }

    const { start, end } = offsets;

    if (checkOverlap(markups, start, end)) {
      alert(
        "This text overlaps with an existing markup. Please select a different portion.",
      );
      selection.removeAllRanges();
      setTooltipMenu(null);
      setSelectedText(null);
      return;
    }

    const anchor = computePopoverAnchor(rect);

    setSelectedText({
      text: selectedTextContent,
      start,
      end,
      rect: {
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY - 10,
      },
    });
    setPendingComment("");
    setPendingReactionKey(null);
    setEditingMarkupId(null);
    setTooltipMenu(anchor);
  }, [readonly, proposalText, markups]);

  const handleMouseUpForSelection = () => {
    if (Date.now() < blockMouseUpUntilRef.current) return;
    handleTextSelection();
  };

  /** Touch selection: iOS/Android need selection to settle; retries match "works after slight scroll". */
  const handleTouchEndForSelection = () => {
    if (readonly || !proposalText) return;
    blockMouseUpUntilRef.current = Date.now() + 450;
    const run = () => handleTextSelection();
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    window.setTimeout(run, 80);
    window.setTimeout(run, 200);
  };

  const updatePopoverPosition = useCallback(() => {
    if (readonly) return;
    const el = textRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer) || range.collapsed) return;
    const rect = range.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) return;
    const anchor = computePopoverAnchor(rect);
    setTooltipMenu((prev) => (prev ? { ...prev, ...anchor } : null));
  }, [readonly]);

  const closePopover = useCallback(() => {
    setTooltipMenu(null);
    setSelectedText(null);
    setPendingComment("");
    setPendingReactionKey(null);
    setEditingMarkupId(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleAnnotationClick = (id: number, event?: React.MouseEvent) => {
    const markup = markups.find((m) => m.id === id);
    if (!markup) return;
    setEditingMarkupId(id);
    setSelectedText({
      text: markup.text,
      start: markup.start,
      end: markup.end,
      rect: {
        x: event?.clientX ?? 0,
        y: event?.clientY ?? 0,
      },
    });
    setPendingReactionKey(markup.reactionKey);
    setPendingComment(markup.comment ?? "");
    setTooltipMenu(
      computePopoverAnchor(
        new DOMRect(
          (event?.clientX ?? 0) - 1,
          (event?.clientY ?? 0) - 12,
          2,
          24,
        ),
      ),
    );
  };

  const applyMarkup = (reactionKey: string) => {
    if (!selectedText) return;

    if (editingMarkupId != null) {
      const nextMarkups = markups.map((m) =>
        m.id === editingMarkupId
          ? {
              ...m,
              reactionKey,
              comment: pendingComment.trim() || undefined,
            }
          : m
      );
      setMarkups(nextMarkups);
    } else {
      const newMarkup: Markup = {
        id: Date.now(),
        start: selectedText.start,
        end: selectedText.end,
        text: selectedText.text,
        reactionKey,
        comment: pendingComment.trim() || undefined,
        createdAt: Date.now(),
      };
      const nextMarkups = [...markups, newMarkup].sort(
        (a, b) => a.start - b.start,
      );
      setMarkups(nextMarkups);
    }

    closePopover();
  };

  const removeMarkup = (id: number) => {
    setMarkups((prev) => prev.filter((m) => m.id !== id));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!tooltipMenu) return;
      const target = event.target as HTMLElement;
      if (target.closest(".tooltip-menu")) return;
      // Allow selecting new text in the proposal area without closing first (esp. mobile touchstart)
      if (textRef.current?.contains(target)) return;
      closePopover();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [tooltipMenu, closePopover]);

  /** Mobile: selection often finalizes after scroll/layout; debounced selectionchange catches it. */
  useEffect(() => {
    if (readonly) return;
    // Browser timer id is a number; Node typings can make ReturnType<typeof setTimeout> = NodeJS.Timeout
    let debounceTimer: number | undefined;
    const onSelectionChange = () => {
      if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        handleTextSelection();
      }, 150);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      if (debounceTimer !== undefined) window.clearTimeout(debounceTimer);
    };
  }, [readonly, handleTextSelection]);

  /** Keep popover aligned when the page or scroll containers move. */
  useEffect(() => {
    if (!tooltipMenu) return;
    window.addEventListener("scroll", updatePopoverPosition, true);
    window.addEventListener("resize", updatePopoverPosition);
    return () => {
      window.removeEventListener("scroll", updatePopoverPosition, true);
      window.removeEventListener("resize", updatePopoverPosition);
    };
  }, [tooltipMenu, updatePopoverPosition]);

  const getStyleForMarkup = (markup: Markup) => {
    const reaction = reactions.find((r) => r.key === markup.reactionKey);
    const label = reaction?.label ?? markup.reactionKey;
    const title = markup.comment
      ? `${label}: ${markup.comment}`
      : readonly
        ? label
        : `${label}: Click to edit`;
    return {
      color: reaction?.color ?? "",
      textColor: reaction?.textColor ?? "",
      label,
      title,
    };
  };

  const isHtml = isHtmlContent(proposalText);
  const plainText = isHtml ? htmlToPlainText(proposalText) : proposalText;

  if (!plainText.trim()) {
    return (
      <div className="p-4 text-sm text-muted-foreground rounded-lg border border-dashed">
        No proposal text configured. Add proposal text in design mode.
      </div>
    );
  }

  const renderContent = () => {
    if (isHtml) {
      return renderAnnotatedHtml(
        proposalText,
        markups,
        getStyleForMarkup,
        handleAnnotationClick,
        readonly,
      );
    }
    return (
      renderAnnotatedText(
        proposalText,
        markups,
        getStyleForMarkup,
        handleAnnotationClick,
        readonly,
      ) || proposalText
    );
  };

  return (
    <div className="space-y-4 p-6 rounded-2xl bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 border-2 border-gray-200 shadow-lg mt-5">
      <div className="space-y-3">
        <label className="block text-start text-base font-semibold text-gray-800">
          {content.label || "Proposal"}
        </label>
        <div
          className={readonly ? "disabled pointer-events-none select-none" : ""}
        >
          <div
            ref={textRef}
            onMouseUp={readonly ? undefined : handleMouseUpForSelection}
            onTouchEnd={readonly ? undefined : handleTouchEndForSelection}
            onContextMenu={readonly ? undefined : (e) => e.preventDefault()}
            dir={effectiveDir}
            className={`p-5 border-2 border-primary/30 rounded-xl bg-white min-h-32 leading-relaxed cursor-text select-text shadow-sm hover:shadow-md transition-shadow text-start touch-manipulation ${isHtml ? "prose prose-sm max-w-none" : ""}`}
            style={{
              userSelect: readonly ? "none" : "text",
              WebkitUserSelect: readonly ? "none" : "text",
              WebkitTouchCallout: "none" as const,
              ...(effectiveDir === "rtl"
                ? { unicodeBidi: "isolate" as const, textAlign: "right" as const }
                : {}),
            }}
          >
            {renderContent()}
          </div>
        </div>

        {/* Comments list - visible in review/readonly mode */}
        {readonly && markups.length > 0 && (
          <div className="mt-4 space-y-2">
            <label className="block text-start text-sm font-semibold text-gray-700">
              Comments
            </label>
            <ul className="space-y-3">
              {markups.map((m) => {
                const reaction = reactions.find((r) => r.key === m.reactionKey);
                return (
                  <li
                    key={m.id}
                    className={`flex flex-col gap-1.5 p-3 rounded-lg border border-gray-200 bg-white text-sm ${reaction?.color ?? ""} ${reaction?.textColor ?? ""}`}
                  >
                    <span className="font-medium">"{m.text}"</span>
                    <span className="text-xs font-medium opacity-90">
                      {reaction?.label ?? m.reactionKey}
                    </span>
                    {m.comment && (
                      <div className="mt-1 pt-1.5 border-t border-gray-200/60">
                        <span className="text-xs font-medium text-gray-600 block mb-0.5">
                          Comment:
                        </span>
                        <span className="text-sm text-gray-800 block">
                          {m.comment}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {tooltipMenu &&
        selectedText &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="tooltip-menu fixed z-[10000] max-h-[min(70vh,24rem)] overflow-y-auto max-w-[min(100vw-1rem,20rem)] bg-white rounded-xl shadow-2xl border-2 border-primary/30 p-3"
            style={{
              left: `${tooltipMenu.x}px`,
              top: `${tooltipMenu.y}px`,
              transform:
                tooltipMenu.placement === "above"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
            }}
          >
            <div className="text-xs font-semibold text-gray-700 mb-2 px-2">
              React:
            </div>
            <div className="flex flex-wrap gap-2 max-w-64 mb-2">
              {reactions.map((reaction) => {
                const isSelected = pendingReactionKey === reaction.key;
                return (
                  <button
                    key={reaction.key}
                    type="button"
                    onClick={() =>
                      setPendingReactionKey(isSelected ? null : reaction.key)
                    }
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all hover:scale-105 shadow-sm ${reaction.color} ${reaction.textColor} hover:shadow-md ${isSelected ? "ring-2 ring-offset-1 ring-primary" : ""}`}
                  >
                    {reaction.label}
                  </button>
                );
              })}
            </div>
            <div className="mb-2">
              <textarea
                value={pendingComment}
                onChange={(e) => setPendingComment(e.target.value)}
                placeholder="Add a comment (optional)..."
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 resize-none max-h-28 overflow-y-auto"
                rows={3}
              />
            </div>
            {!pendingReactionKey && (
              <p className="text-[11px] text-gray-400 text-center mb-1">
                Select a reaction above to apply
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                if (pendingReactionKey) {
                  applyMarkup(pendingReactionKey);
                }
              }}
              disabled={!pendingReactionKey}
              className="w-full py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
            >
              {editingMarkupId != null ? "Update" : "Apply"}
            </button>
            {editingMarkupId != null && (
              <button
                type="button"
                onClick={() => {
                  removeMarkup(editingMarkupId);
                  closePopover();
                }}
                className="w-full py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 mt-1"
              >
                Remove highlight
              </button>
            )}
            <button
              type="button"
              onClick={closePopover}
              className="absolute -top-2 -right-2 w-7 h-7 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-full flex items-center justify-center text-xs hover:from-red-600 hover:to-rose-600 shadow-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TextMarkup;
